const STORAGE_KEY = "familyDashboardActionQueue";
const MAX_QUEUE_ITEMS = 250;
const MAX_ACTION_AGE_MS = 7 * 24 * 60 * 60 * 1000;
let isProcessing = false;

const safeParse = (value) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("Action queue parse failed", error);
    return [];
  }
};

const loadQueue = () => {
  if (typeof window === "undefined" || !window.localStorage) return [];
  const parsed = safeParse(window.localStorage.getItem(STORAGE_KEY));
  const now = Date.now();
  const fresh = parsed.filter((item) => {
    const queuedAt = Number(item?.queuedAt || 0);
    return queuedAt > 0 && now - queuedAt <= MAX_ACTION_AGE_MS;
  });
  return fresh.slice(-MAX_QUEUE_ITEMS);
};

const saveQueue = (queue) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  const now = Date.now();
  const fresh = (Array.isArray(queue) ? queue : [])
    .filter((item) => {
      const queuedAt = Number(item?.queuedAt || 0);
      return queuedAt > 0 && now - queuedAt <= MAX_ACTION_AGE_MS;
    })
    .slice(-MAX_QUEUE_ITEMS);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
};

const isRetryableError = (error) => {
  const code = error?.code || "";
  const message = error?.message || "";
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  return (
    offline ||
    code.includes("unavailable") ||
    code.includes("network") ||
    message.toLowerCase().includes("network")
  );
};

export const enqueueAction = (action) => {
  if (!action) return;
  const queue = loadQueue();
  queue.push({
    ...action,
    queuedAt: Date.now()
  });
  saveQueue(queue);
};

export const processQueue = async (handler) => {
  if (isProcessing) return;
  if (typeof handler !== "function") return;
  const queue = loadQueue();
  if (queue.length === 0) return;

  isProcessing = true;
  const remaining = [];

  for (const action of queue) {
    try {
      await handler(action);
    } catch (error) {
      if (isRetryableError(error)) {
        remaining.push(action);
      } else {
        console.warn("Dropping action from queue", action, error);
      }
    }
  }

  saveQueue(remaining);
  isProcessing = false;
};

export const runActionWithQueue = async (action, handler) => {
  try {
    await handler(action);
    return { queued: false };
  } catch (error) {
    if (isRetryableError(error)) {
      enqueueAction(action);
      return { queued: true };
    }
    throw error;
  }
};

export const initActionQueue = (handler, { intervalMs = 15000 } = {}) => {
  if (typeof window === "undefined") return () => {};
  const handleOnline = () => processQueue(handler);
  window.addEventListener("online", handleOnline);
  const interval = setInterval(() => processQueue(handler), intervalMs);
  processQueue(handler);

  return () => {
    window.removeEventListener("online", handleOnline);
    clearInterval(interval);
  };
};
